import type { PlatformChurchDetail } from "@/api/platform";

type ChurchAdministratorsPanelProps = {
  church: PlatformChurchDetail;
};

export function ChurchAdministratorsPanel({
  church,
}: ChurchAdministratorsPanelProps) {
  return (
    <div className="space-y-6">
      <SectionHeading
        title="Administrators"
        description="Users currently assigned administrative access."
      />

      <div className="space-y-3">
        {church.administrators.length === 0 ? (
          <EmptyDetail message="No administrators are assigned." />
        ) : (
          church.administrators.map((administrator) => (
            <div
              key={administrator.id}
              className="flex flex-col justify-between gap-3 rounded-xl border border-slate-200 p-4 sm:flex-row sm:items-center"
            >
              <div>
                <p className="font-semibold text-slate-950">
                  {formatAdministratorName(administrator)}
                </p>

                <p className="mt-1 text-sm text-slate-500">
                  {administrator.email}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  {formatValue(administrator.role)}
                </span>

                <StatusBadge
                  value={
                    administrator.is_active
                      ? "ACTIVE"
                      : "INACTIVE"
                  }
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SectionHeading({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-slate-950">
        {title}
      </h3>

      <p className="mt-1 text-sm leading-6 text-slate-500">
        {description}
      </p>
    </div>
  );
}

function EmptyDetail({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
      {message}
    </div>
  );
}

function StatusBadge({ value }: { value: string }) {
  const normalized = value.toUpperCase();

  const className =
    normalized === "ACTIVE"
      ? "bg-emerald-100 text-emerald-700"
      : "bg-rose-100 text-rose-700";

  return (
    <span
      className={[
        "inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-semibold",
        className,
      ].join(" ")}
    >
      {formatValue(value)}
    </span>
  );
}

function formatValue(value?: string | null) {
  if (!value) return "Not available";

  return value
    .toLowerCase()
    .split("_")
    .map(
      (part) =>
        part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join(" ");
}

function formatAdministratorName(
  administrator: PlatformChurchDetail["administrators"][number]
) {
  const fullName = [
    administrator.first_name,
    administrator.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || administrator.email;
}